import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { RotateCcw, Table2, X } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import SegmetricsTable from '~/containers/workflows/verify/SegmetricsTable';
import { replanningQaMeasurementState } from '~/state/atoms/adaptiveReplanningAtoms';
import { Note, Progress } from './QaStageNotes';
import { useRunDeformation } from './useRunDeformation';

interface NameListProps {
    readonly id: string;
    readonly names: readonly string[];
    readonly testId: string;
}

function NameList({ id, names, testId }: NameListProps) {
    if (names.length === 0) {
        return null;
    }
    return (
        <Typography fontSize={11} color="text.secondary" data-testid={testId}>
            <FormattedMessage id={id} values={{ names: names.join(', ') }} />
        </Typography>
    );
}

export default function RegistrationQaMeasurement() {
    const intl = useIntl();
    const measurement = useRecoilValue(replanningQaMeasurementState);
    const retry = useResetRecoilState(replanningQaMeasurementState);
    const deformation = useRunDeformation();
    const [tableOpen, setTableOpen] = useState(false);

    if (!deformation) {
        return (
            <Note
                id="replanning.qa.metricsUnavailable"
                testId="replanning-qa-metrics-unavailable"
            />
        );
    }
    if (!measurement || measurement.status === 'measuring') {
        return (
            <Progress
                id="replanning.qa.measuring"
                testId="replanning-qa-measuring"
            />
        );
    }
    if (measurement.status === 'failed') {
        return (
            <Stack spacing={0.75} alignItems="flex-start">
                <Note
                    id={measurement.messageId ?? 'replanning.qa.measureFailed'}
                    testId="replanning-qa-measure-failed"
                    tone="warning.main"
                    detail={measurement.detail}
                />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RotateCcw size={12} />}
                    onClick={retry}
                    data-testid="replanning-qa-measure-retry"
                    sx={{ fontSize: 11, textTransform: 'none' }}
                >
                    <FormattedMessage id="replanning.qa.retry" />
                </Button>
            </Stack>
        );
    }
    const { coverage, rows } = measurement;
    return (
        <Stack spacing={1} data-testid="replanning-qa-metrics">
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
            >
                <Typography fontSize={11} fontWeight={600}>
                    <FormattedMessage
                        id="replanning.qa.measuredCount"
                        values={{ count: rows.length }}
                    />
                </Typography>
                <Button
                    size="small"
                    startIcon={<Table2 size={12} />}
                    onClick={() => setTableOpen(true)}
                    disabled={rows.length === 0}
                    data-testid="replanning-qa-open-table"
                    sx={{ fontSize: 11, textTransform: 'none', py: 0 }}
                >
                    <FormattedMessage id="replanning.qa.allMetrics" />
                </Button>
            </Box>
            <NameList
                id="replanning.qa.onlyTarget"
                names={coverage.onlyTarget}
                testId="replanning-qa-only-target"
            />
            <NameList
                id="replanning.qa.onlyPlanning"
                names={coverage.onlyPlanning}
                testId="replanning-qa-only-planning"
            />
            <NameList
                id="replanning.qa.notMeasured"
                names={coverage.notMeasured}
                testId="replanning-qa-not-measured"
            />
            <Dialog
                open={tableOpen}
                onClose={() => setTableOpen(false)}
                maxWidth="lg"
                fullWidth
                aria-labelledby="replanning-qa-table-title"
            >
                <DialogTitle
                    id="replanning-qa-table-title"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: 14,
                        py: 1.25,
                    }}
                >
                    <FormattedMessage id="replanning.qa.tableTitle" />
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({ id: 'close' })}
                        onClick={() => setTableOpen(false)}
                        data-testid="replanning-qa-close-table"
                    >
                        <X size={16} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    <SegmetricsTable data={[...rows]} maxHeight={560} />
                </DialogContent>
            </Dialog>
        </Stack>
    );
}
